import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from 'api';
import { ChatSessionFile } from 'models/Message';
import { PaginatedData } from 'models/PaginatedData';
import { getArrayLength } from 'utils';
import useInfinitePagination from 'hooks/useInfinitePagination';
import useCallbackRef from 'hooks/useCallbackRef';
import useBusEvent from 'hooks/useBusEvent';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { NewMessageFilesEventData } from 'services/socketService/socketEventData';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import MessageFilePreviewModal from 'components/MessageFiles/components/MessageFilePreviewModal';
import SaveChatFilesToDataRoomToast from '../../components/SaveChatFilesToDataRoomToast';
import MessagingContext from '../Messaging/Messaging.context';
import ChatFilesContext from './ChatFiles.context';

const DEFAULT_PARAMS = { $limit: '20', $sort: '-createdAt' };

// what a page of another chat's files is worth here: `useInfinitePagination` merges
// whatever a request hands back, so a late response is dropped rather than returned
const EMPTY_FILES_PAGE: PaginatedData<ChatSessionFile> = {
  items: [],
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
};

// a file opened from a message bubble may sit deep in the (paginated) session file list.
// We page forward to find it so the arrows can walk the whole list, but only so far —
// beyond this the file is previewed on its own, without navigation.
const PAGES_LOOKUP_LIMIT = 10;

// how close to the end of the loaded list the arrows may get before the next page is
// pulled in, so navigating never stalls on a request
const FILES_LOOKAHEAD = 5;

/**
 * Owns the chat session's file list and the single file preview modal shared by the
 * "Files in chat" sidebar and message attachments, so the preview arrows navigate the
 * whole session no matter where the file was opened from.
 */
const ChatFiles: React.FC = (props) => {
  const { children } = props;

  const { sessionId } = useContext(MessagingContext);

  const [previewModal, previewModalRef] = useCallbackRef<OverlayRef>();

  const [activeFileId, setActiveFileId] = useState<string>();
  const [openedFileName, setOpenedFileName] = useState<string>();
  const [isLookingUpFile, setIsLookingUpFile] = useState(false);
  const [pendingNextIndex, setPendingNextIndex] = useState<number>();
  const [totalFiles, setTotalFiles] = useState(0);
  const [loadedSessionId, setLoadedSessionId] = useState<string>();

  // which chat a response belongs to is decided when it lands, not when it was requested
  const currentSessionId = useRef(sessionId);

  const {
    loading,
    items: files,
    setItems: setFiles,
    currentPage,
    shouldContinuePagination,
    setCurrentPage,
    onContainerScrolled,
  } = useInfinitePagination<ChatSessionFile>({
    condition: !!sessionId,
    resetDeps: [sessionId],
    dependencies: [sessionId],
    makeRequest: async (currentPage) => {
      const requestedSessionId = sessionId;

      try {
        const pagination = await api.messages
          .getMessagesFiles(sessionId, {
            $page: currentPage.toString(),
            ...DEFAULT_PARAMS,
          })
          .then((res) => res.data);

        // the chat may have been switched while this was in flight — the hook skips its
        // own reset whenever a request is running, so these files would be merged into
        // the new chat's list
        if (requestedSessionId !== currentSessionId.current)
          return EMPTY_FILES_PAGE;

        setTotalFiles(pagination.totalItems);

        return pagination;
      } finally {
        // the list is only known to be empty once a page of *this* chat has come back;
        // a failed request ends that first load too, rather than leaving placeholders
        if (requestedSessionId === currentSessionId.current)
          setLoadedSessionId(requestedSessionId);
      }
    },
  });

  // an empty list means "no files in this chat" only once we've heard back about it —
  // until then the panel keeps loading instead of claiming the chat has none
  const loadingFiles = loading || loadedSessionId !== sessionId;

  const loadedFiles = getArrayLength(files);

  const activeIndex = useMemo(
    () => files.findIndex(({ _id }) => _id === activeFileId),
    [files, activeFileId],
  );

  const activeFileName = useMemo(
    () => (activeIndex === -1 ? openedFileName : files[activeIndex].file.name),
    [activeIndex, files, openedFileName],
  );

  const updateFile = useCallback(
    (file: Partial<ChatSessionFile>) =>
      setFiles((old) =>
        old.map((el) => (el._id === file._id ? { ...el, ...file } : el)),
      ),
    [setFiles],
  );

  const removeFile = useCallback(
    (fileId: string) =>
      setFiles((old) => old.filter(({ _id }) => _id !== fileId)),
    [setFiles],
  );

  const addFiles = useCallback(
    (chatSessionFiles: ChatSessionFile[]) => {
      setFiles((old) => [...chatSessionFiles, ...old]);
      setTotalFiles((old) => old + getArrayLength(chatSessionFiles));
    },
    [setFiles],
  );

  // The bulk route also hands back the created nodes, which the single one does not,
  // so the two are kept apart until the bulk route ships — routing the "Files in chat"
  // menu through it would take a working action down with it.
  const onFilesAddToDataRoom = useCallback(
    async (fileIds: string[]) => {
      const { data } = await api.messages.moveFilesToDataRoom(
        sessionId,
        fileIds,
      );

      fileIds.forEach((_id) => updateFile({ _id, isDataRoomFile: true }));

      return data;
    },
    [sessionId, updateFile],
  );

  const onFileAddToDataRoom = useCallback(
    async (_id: string) => {
      await api.messages.moveFileToDataRoom(sessionId, _id);
      updateFile({ _id, isDataRoomFile: true });
    },
    [sessionId, updateFile],
  );

  const onFileRemove = useCallback(
    async (_id: string, removeFromDataRoom?: boolean) => {
      await api.messages.removeMessageFile(sessionId, _id, removeFromDataRoom);

      bus.broadcastEvent(StandardEventTypes.FILE_DELETE, _id);
      removeFile(_id);

      setTotalFiles((old) => Math.max(old - 1, 0));
    },
    [sessionId, removeFile],
  );

  const onFilePreview = useCallback(
    (id: string) => api.messages.previewMessageFile(sessionId, id),
    [sessionId],
  );

  // `onContainerScrolled` only bumps the page number, so calling it again before the
  // request starts queues two page loads — and `useInfinitePagination` drops all but the
  // last, leaving a hole in the list. Keying on the loaded count makes one request per
  // batch of files we've actually received.
  const requestedAtCount = useRef(-1);

  const loadMoreFiles = useCallback(() => {
    if (loading || !shouldContinuePagination) return;
    if (requestedAtCount.current === loadedFiles) return;

    requestedAtCount.current = loadedFiles;
    onContainerScrolled();
  }, [loading, shouldContinuePagination, loadedFiles, onContainerScrolled]);

  // the list belongs to one chat: switching takes the files, the total, the open preview
  // and the request bookkeeping with it. The hook's own reset can't be relied on — it is
  // skipped while a request is in flight, which is exactly when a chat is switched.
  useEffect(() => {
    currentSessionId.current = sessionId;
    requestedAtCount.current = -1;

    setActiveFileId(undefined);
    setTotalFiles(0);
    setFiles([]);
    setCurrentPage(1);
  }, [sessionId, setFiles, setCurrentPage]);

  // the lookup effect below clears the flag right away when the file is already loaded
  const openFilePreview = useCallback(
    (fileId: string, fileName?: string) => {
      setActiveFileId(fileId);
      setOpenedFileName(fileName);
      setIsLookingUpFile(true);
      setPendingNextIndex(undefined);

      previewModal?.open();
    },
    [previewModal],
  );

  const onPrev = useCallback(() => {
    if (activeIndex === -1 || !loadedFiles) return;

    setActiveFileId(
      files[activeIndex === 0 ? loadedFiles - 1 : activeIndex - 1]._id,
    );
  }, [activeIndex, files, loadedFiles]);

  const onNext = useCallback(() => {
    if (activeIndex === -1 || !loadedFiles) return;

    if (activeIndex < loadedFiles - 1) {
      setActiveFileId(files[activeIndex + 1]._id);
      return;
    }

    // at the very end of what's loaded (the lookahead below normally prevents this):
    // wait for the next page, otherwise wrap back to the newest file
    if (shouldContinuePagination) {
      setPendingNextIndex(loadedFiles);
      loadMoreFiles();
      return;
    }

    setActiveFileId(files[0]._id);
  }, [activeIndex, files, loadedFiles, shouldContinuePagination, loadMoreFiles]);

  // keep the list loading ahead of the arrows
  useEffect(() => {
    if (activeIndex === -1 || activeIndex < loadedFiles - FILES_LOOKAHEAD)
      return;

    loadMoreFiles();
  }, [activeIndex, loadedFiles, loadMoreFiles]);

  useEffect(() => {
    if (pendingNextIndex === undefined) return;

    if (loadedFiles > pendingNextIndex) {
      setActiveFileId(files[pendingNextIndex]._id);
      setPendingNextIndex(undefined);
      return;
    }

    if (!loading && !shouldContinuePagination) setPendingNextIndex(undefined);
  }, [pendingNextIndex, loadedFiles, files, loading, shouldContinuePagination]);

  useEffect(() => {
    if (!isLookingUpFile) return;

    if (activeIndex !== -1) {
      setIsLookingUpFile(false);
      return;
    }

    // give up once there's nothing left to load or we've paged far enough — the file is
    // then previewed on its own, without arrows
    if (!shouldContinuePagination || currentPage >= PAGES_LOOKUP_LIMIT) {
      if (!loading) setIsLookingUpFile(false);
      return;
    }

    loadMoreFiles();
  }, [
    isLookingUpFile,
    loading,
    activeIndex,
    currentPage,
    shouldContinuePagination,
    loadMoreFiles,
  ]);

  const newMessageFiles = useCallback(
    ({
      sessionId: sessionIdEvent,
      chatSessionFiles,
    }: NewMessageFilesEventData) => {
      if (sessionIdEvent !== sessionId) return;

      addFiles(chatSessionFiles);
    },
    [sessionId, addFiles],
  );

  const newFilesSent = useCallback(
    ({ payload }: CallbackBusType<ChatSessionFile[]>) => addFiles(payload),
    [addFiles],
  );

  const onMessageDelete = useCallback(
    ({ payload }: CallbackBusType<{ chatSessionFileId: string }>) =>
      removeFile(payload.chatSessionFileId),
    [removeFile],
  );

  useBusEvent(StandardEventTypes.FILES_SENT, newFilesSent);

  useSocketEvent(LISTEN_EVENTS.NEW_MESSAGE_FILES, newMessageFiles);

  useBusEvent(StandardEventTypes.FILE_DELETED_WITH_MESSAGE, onMessageDelete);

  // navigation is only possible once we know where the file sits in the list
  const canNavigate = activeIndex !== -1 && loadedFiles > 1;

  // memoized so that opening / arrowing through the preview doesn't re-render every
  // consumer (each chat message is one)
  const value = useMemo(
    () => ({
      files,
      loading: loadingFiles,
      totalFiles,
      onContainerScrolled: loadMoreFiles,
      onFilePreview,
      onFileAddToDataRoom,
      onFilesAddToDataRoom,
      onFileRemove,
      openFilePreview,
    }),
    [
      files,
      loadingFiles,
      totalFiles,
      loadMoreFiles,
      onFilePreview,
      onFileAddToDataRoom,
      onFilesAddToDataRoom,
      onFileRemove,
      openFilePreview,
    ],
  );

  return (
    <ChatFilesContext.Provider value={value}>
      {children}

      <MessageFilePreviewModal
        ref={previewModalRef}
        fileId={activeFileId}
        fileName={activeFileName}
        scope={sessionId}
        onFilePreview={onFilePreview}
        {...(canNavigate && { onPrev, onNext })}
      />

      <SaveChatFilesToDataRoomToast />
    </ChatFilesContext.Provider>
  );
};

export default ChatFiles;
